import type { TFunction } from 'i18next';
import type { AdminOrderItem } from '@/services/adminService';
import { calculateCurrentDpi, formatPpi } from '@/components/order/utils/printSizeForm';
import { PRINT_QUALITY_MIN_PPI } from '@/components/order/utils/printQualityWarning';
import { convertInchesToUnit, unitPrecision } from '@/utils/measurement';
import type { MeasurementUnit } from '@/types/measurement';

export const formatMeasurement = (
  value: number | null | undefined,
  unit: MeasurementUnit,
  digits = unitPrecision(unit),
): string => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return convertInchesToUnit(value, unit).toFixed(digits);
};

export const formatDimensions = (
  width: number | null,
  height: number | null,
  unit: MeasurementUnit,
  digits = unitPrecision(unit),
): string => {
  if (width == null && height == null) {
    return '—';
  }
  const safeWidth = formatMeasurement(width, unit, digits);
  const safeHeight = formatMeasurement(height, unit, digits);
  return `${safeWidth} x ${safeHeight}`;
};

export const appendUnitLabel = (value: string, unitLabel: string): string => (
  value === '—' ? value : `${value} ${unitLabel}`
);

export const formatPixelSize = (widthPx: number | null, heightPx: number | null): string => {
  if (widthPx == null && heightPx == null) {
    return '—';
  }
  const safeWidth = widthPx != null ? `${widthPx}px` : '—';
  const safeHeight = heightPx != null ? `${heightPx}px` : '—';
  return `${safeWidth} x ${safeHeight}`;
};

export const formatOriginalPhysicalSize = (
  item: AdminOrderItem,
  unit: MeasurementUnit,
  digits = unitPrecision(unit),
): string | null => {
  const widthPx = item.uploadedFile?.originalWidthPx ?? null;
  const heightPx = item.uploadedFile?.originalHeightPx ?? null;
  const dpiX = item.uploadedFile?.dpiX ?? null;
  const dpiY = item.uploadedFile?.dpiY ?? null;

  if (
    widthPx == null
    || heightPx == null
    || dpiX == null
    || dpiY == null
    || !Number.isFinite(dpiX)
    || !Number.isFinite(dpiY)
    || dpiX <= 0
    || dpiY <= 0
  ) {
    return null;
  }

  return formatDimensions(widthPx / dpiX, heightPx / dpiY, unit, digits);
};

export const getFullPrintableSizeInches = (item: AdminOrderItem): { width: number | null; height: number | null } => ({
  width: item.widthIn == null ? null : item.widthIn + Math.max(0, item.galleryWrapIn ?? 0) * 2,
  height: item.heightIn == null ? null : item.heightIn + Math.max(0, item.galleryWrapIn ?? 0) * 2,
});

export const getCurrentPrintDpi = (item: AdminOrderItem): { ppiX: number | null; ppiY: number | null } => {
  const widthPx = item.uploadedFile?.originalWidthPx ?? null;
  const heightPx = item.uploadedFile?.originalHeightPx ?? null;
  const { dpiX, dpiY } = calculateCurrentDpi({
    pixelWidth: widthPx,
    pixelHeight: heightPx,
    widthIn: item.widthIn,
    heightIn: item.heightIn,
    marginIn: item.marginIn,
  });

  return { ppiX: dpiX, ppiY: dpiY };
};

export const getVisibleArtSizeInches = (item: AdminOrderItem): { width: number | null; height: number | null } => ({
  width: item.widthIn == null ? null : Math.max(0, item.widthIn - Math.max(0, item.marginIn ?? 0) * 2),
  height: item.heightIn == null ? null : Math.max(0, item.heightIn - Math.max(0, item.marginIn ?? 0) * 2),
});

export const formatPrintPpi = (item: AdminOrderItem): string => {
  const { ppiX, ppiY } = getCurrentPrintDpi(item);
  return formatPpi(ppiX, ppiY);
};

export const formatLowPpiBadge = (item: AdminOrderItem): string | null => {
  const { ppiX, ppiY } = getCurrentPrintDpi(item);
  const threshold = item.printQualityWarning?.threshold ?? PRINT_QUALITY_MIN_PPI;
  const currentPpi = [ppiX, ppiY].filter((value): value is number => value != null);
  if (currentPpi.length > 0) {
    const ppi = Math.min(...currentPpi);
    if (ppi >= threshold) {
      return null;
    }
    return item.printQualityWarning?.acknowledged === true
      ? `Low res accepted: ${ppi} PPI`
      : `Low res warning: ${ppi} PPI`;
  }

  const warning = item.printQualityWarning;
  if (warning?.type !== 'LOW_PPI') {
    return null;
  }
  const ppi = Math.round(warning.ppi);
  return warning.acknowledged === true ? `Low res accepted: ${ppi} PPI` : `Low res warning: ${ppi} PPI`;
};

export const formatLowPpiTitle = (item: AdminOrderItem): string | undefined => {
  const { ppiX, ppiY } = getCurrentPrintDpi(item);
  const threshold = item.printQualityWarning?.threshold ?? PRINT_QUALITY_MIN_PPI;
  const currentPpi = [ppiX, ppiY].filter((value): value is number => value != null);
  if (currentPpi.length > 0) {
    const ppi = Math.min(...currentPpi);
    if (ppi >= threshold) {
      return undefined;
    }
    const acknowledgement = item.printQualityWarning?.acknowledged === true
      ? 'User accepted this low-resolution print quality warning.'
      : 'Low-resolution print quality warning has not been acknowledged.';
    return `${acknowledgement} Current print quality is ${ppi} PPI. Recommended minimum is ${threshold} PPI.`;
  }

  const warning = item.printQualityWarning;
  if (warning?.type !== 'LOW_PPI') {
    return undefined;
  }
  const acknowledgement = warning.acknowledged === true
    ? 'User accepted this low-resolution print quality warning.'
    : 'Low-resolution print quality warning has not been acknowledged.';
  return `${acknowledgement} Recommended minimum is ${warning.threshold} PPI.`;
};

export const formatPrice = (
  value: number | null,
  currencyCode: 'USD' | 'UAH' = 'USD',
  usdToUahRate: number | null = null,
  locale?: string,
): string => {
  return formatCurrencyAmount(value, 'USD', currencyCode, usdToUahRate, locale);
};

export const formatCurrencyAmount = (
  value: number | null,
  sourceCurrencyCode: string | null,
  targetCurrencyCode: 'USD' | 'UAH',
  usdToUahRate: number | null = null,
  locale?: string,
): string => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }

  const sourceCurrency = sourceCurrencyCode?.toUpperCase() ?? null;
  const hasValidRate = usdToUahRate != null && Number.isFinite(usdToUahRate) && usdToUahRate > 0;

  const resolvedValue = sourceCurrency == null || sourceCurrency === targetCurrencyCode
    ? value
    : sourceCurrency === 'USD' && targetCurrencyCode === 'UAH'
      ? (hasValidRate ? value * usdToUahRate : null)
      : sourceCurrency === 'UAH' && targetCurrencyCode === 'USD'
        ? (hasValidRate ? value / usdToUahRate : null)
        : null;

  if (resolvedValue == null) {
    return '—';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: targetCurrencyCode,
      maximumFractionDigits: targetCurrencyCode === 'UAH' ? 0 : 2,
    }).format(resolvedValue);
  } catch {
    return targetCurrencyCode + ' ' + resolvedValue.toFixed(targetCurrencyCode === 'UAH' ? 0 : 2);
  }
};

export const formatAdminDateParts = (value: string | null | undefined): { date: string; time: string } => {
  if (!value) {
    return { date: '—', time: '—' };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: '—', time: '—' };
  }

  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return {
    date: yyyy + '-' + mm + '-' + dd,
    time: hh + ':' + min,
  };
};

export const formatAdminDateTime = (value: string | null | undefined): string => {
  const parts = formatAdminDateParts(value);
  if (parts.date === '—') {
    return '—';
  }

  return parts.date.replace(/-/g, '/') + ' ' + parts.time;
};

const READABLE_CODE_SUFFIX_LENGTH = 3;
const READABLE_CODE_DECIMAL_LENGTH = 5;

export const formatReadableNumericCode = (value: string, suffixLength = READABLE_CODE_SUFFIX_LENGTH): string => {
  const normalized = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-suffixLength).padStart(suffixLength, '0');
  const decimalValue = Number.parseInt(normalized, 36);
  if (!Number.isFinite(decimalValue)) {
    return ''.padStart(READABLE_CODE_DECIMAL_LENGTH, '0');
  }
  return String(decimalValue).padStart(READABLE_CODE_DECIMAL_LENGTH, '0');
};

export const formatOrderItemReadableCode = (itemId: string): string => (
  formatReadableNumericCode(itemId, READABLE_CODE_SUFFIX_LENGTH)
);

export const getUnitLabel = (t: TFunction, unit: MeasurementUnit): string => {
  return unit === 'cm' ? t('measurement.cm', 'cm') : t('measurement.in', 'in');
};

export const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDefaultConfirmedDateRange = (): { dateFrom: string; dateTo: string } => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  return {
    dateFrom: formatDateInputValue(yesterday),
    dateTo: formatDateInputValue(today),
  };
};

const parseDateAtBoundary = (dateValue: string, boundary: 'start' | 'end'): Date | null => {
  if (!dateValue) {
    return null;
  }

  const [yearRaw, monthRaw, dayRaw] = dateValue.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  return boundary === 'start'
    ? new Date(year, month - 1, day, 0, 0, 0, 0)
    : new Date(year, month - 1, day, 23, 59, 59, 999);
};

export const isTimestampWithinDateRange = (
  timestamp: string | null | undefined,
  dateFrom: string,
  dateTo: string,
): boolean => {
  if (!timestamp) {
    return false;
  }

  const sourceDate = new Date(timestamp);
  if (Number.isNaN(sourceDate.getTime())) {
    return false;
  }

  const fromDate = parseDateAtBoundary(dateFrom, 'start');
  const toDate = parseDateAtBoundary(dateTo, 'end');

  if (fromDate && sourceDate < fromDate) {
    return false;
  }

  if (toDate && sourceDate > toDate) {
    return false;
  }

  return true;
};


export const normalizeSearchInput = (value: string): string => (
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
);

export const matchesSearchTerms = (terms: ReadonlyArray<string>, values: ReadonlyArray<string | null | undefined>): boolean => {
  if (terms.length === 0) {
    return true;
  }

  const haystack = normalizeSearchInput(values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0).join(' '));
  if (!haystack) {
    return false;
  }

  return terms.every((term) => haystack.includes(term));
};
